import React, { useState, useEffect } from "react";
import { Row, Col, FormGroup, Label, Input, Button } from "reactstrap";
import { Filter, X } from "react-feather";
import Select from "react-select";
import { projectService } from "../../services/projectService";

/**
 * PointsFilter - Filter component for points view
 */
const PointsFilter = ({ filters, onApplyFilters, onClearFilters }) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [projects, setProjects] = useState([]);
  const [loadingProjects, setLoadingProjects] = useState(false);

  // Fetch projects on mount
  useEffect(() => {
    const fetchProjects = async () => {
      setLoadingProjects(true);
      try {
        const response = await projectService.list({ page: 0, size: 1000 });
        const projectOptions = (response.content || []).map((project) => ({
          value: project.projId || project.projName,
          label: `${project.projName} - ${project.recDisplayName || project.deviceId}`,
          data: project,
        }));
        setProjects(projectOptions);
      } catch (error) {
        console.error("Error fetching projects:", error);
        setProjects([]);
      } finally {
        setLoadingProjects(false);
      }
    };
    fetchProjects();
  }, []);

  const handleChange = (field, value) => {
    setLocalFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleProjectChange = (selectedOption) => {
    setLocalFilters((prev) => ({
      ...prev,
      projId: selectedOption ? selectedOption.value : "",
    }));
  };

  const handleApply = () => {
    onApplyFilters(localFilters);
  };

  const handleClear = () => {
    const defaultFilters = {
      projId: "",
      projName: "",
      pointDisplayName: "",
      sortBy: "pointId",
      sortDir: "asc",
    };
    setLocalFilters(defaultFilters);
    onClearFilters();
  };

  return (
    <div className="points-filter mb-3 p-3 border rounded">
      <h6 className="mb-3">
        <Filter size={16} className="me-2" />
        Filters
      </h6>
      <Row>
        <Col md="3">
          <FormGroup>
            <Label for="filterPointDisplayName">Point Display Name</Label>
            <Input
              id="filterPointDisplayName"
              type="text"
              placeholder="Search by display name..."
              value={localFilters.pointDisplayName || ""}
              onChange={(e) => handleChange("pointDisplayName", e.target.value)}
            />
          </FormGroup>
        </Col>
        <Col md="3">
          <FormGroup>
            <Label for="filterProjId">Project</Label>
            <Select
              id="filterProjId"
              options={projects}
              value={projects.find((p) => p.value === localFilters.projId) || null}
              onChange={handleProjectChange}
              isClearable
              isSearchable
              isLoading={loadingProjects}
              placeholder="Select a project..."
              styles={{
                control: (base) => ({
                  ...base,
                  minHeight: "38px",
                }),
              }}
            />
          </FormGroup>
        </Col>
        <Col md="2">
          <FormGroup>
            <Label for="filterSortBy">Sort By</Label>
            <Input
              id="filterSortBy"
              type="select"
              value={localFilters.sortBy || "pointId"}
              onChange={(e) => handleChange("sortBy", e.target.value)}
            >
              <option value="pointId">Point ID</option>
              <option value="pointDisplayName">Display Name</option>
              <option value="projId">Project ID</option>
              <option value="projName">Project Name</option>
            </Input>
          </FormGroup>
        </Col>
        <Col md="1">
          <FormGroup>
            <Label for="filterSortDir">Order</Label>
            <Input
              id="filterSortDir"
              type="select"
              value={localFilters.sortDir || "asc"}
              onChange={(e) => handleChange("sortDir", e.target.value)}
            >
              <option value="asc">Asc</option>
              <option value="desc">Desc</option>
            </Input>
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex align-items-center gap-2">
          <Button
            color="primary"
            onClick={handleApply}
            title="Apply Filters"
          >
            <Filter size={14} className="me-1" />
            Apply
          </Button>
          <Button
            color="secondary"
            size="sm"
            outline
            onClick={handleClear}
          >
            <X size={14} className="me-1" />
            Clear Filters
          </Button>
        </Col>
      </Row>
    </div>
  );
};

export default PointsFilter;
